/**
 * Ako sa kreslia karty. Nie je to len vkus:
 *
 * - `art` — pôvodný obrázok. Verný, ale text je na malej karte nečitateľný
 *   a pri slabšom skene sa rozpadne úplne.
 * - `hybrid` — obrázok zostane, spodná časť s menom a textom sa prekreslí
 *   ostrým HTML. Rieši presne tie zlé obrázky a pritom nechá kartu vyzerať
 *   ako karta. Predvolené.
 * - `tooltip` — obrázok zostane nedotknutý, ale pri prejdení myšou sa vedľa
 *   objaví popis: čo tá hodnota znamená, kedy sa karta zahráva, kto ju smie
 *   zahrať a čo urobí.
 * - `template` — bez obrázka, všetko z dát. Najlepšie čitateľné a jediné,
 *   čo raz pôjde preložiť.
 *
 * Voľba je vec jedného človeka, drží sa v prehliadači a chodí kontextom, aby
 * ju nebolo treba vliecť cez pol aplikácie ako prop.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Locale } from '@unmatched/content';

export type CardVariant = 'art' | 'hybrid' | 'tooltip' | 'template';

export const VARIANT_LABEL: Record<CardVariant, string> = {
  art: 'Obrázok',
  hybrid: 'Obrázok + text',
  tooltip: 'Obrázok + popis',
  template: 'Iba text',
};

interface CardDisplay {
  variant: CardVariant;
  /** Jazyk textu kariet. Zatiaľ je preložená len angličtina, viď `content/i18n`. */
  locale: Locale;
  setVariant: (variant: CardVariant) => void;
}

const KEY = 'unmatched:cardVariant';

function readVariant(): CardVariant {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === 'art' || stored === 'template' || stored === 'hybrid' || stored === 'tooltip'
      ? stored
      : 'hybrid';
  } catch {
    return 'hybrid';
  }
}

const Ctx = createContext<CardDisplay>({ variant: 'hybrid', locale: 'en', setVariant: () => {} });

export function CardDisplayProvider({ children }: { children: ReactNode }) {
  const [variant, setStored] = useState<CardVariant>(readVariant);

  const setVariant = useCallback((next: CardVariant) => {
    setStored(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* bez úložiska sa voľba len neprenesie cez refresh */
    }
  }, []);

  const value = useMemo<CardDisplay>(() => ({ variant, locale: 'en', setVariant }), [variant, setVariant]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCardDisplay(): CardDisplay {
  return useContext(Ctx);
}
